import Page from 'src/shared/components/Page';
import { Card, CardContent, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SubheaderWithButton from './SubheaderWithButton';
import { WrappedComponent as EmployeeDetailsContent } from './EmployeeDetailsContent';
import { Form } from 'react-final-form';
import { PageWithTitleError, PageWithTitleLoading } from 'src/shared/components/PageWithTitle';
import { type Employee } from 'src/shared/types/employee';
import {
  type ContractPayflowStatus,
  getStatusToDisplay,
  ContractFlexflowStatusToDisplay
} from '../Contracts/ContractStatusAfterInvitation';
import { type FlexflowCardStatus } from '../types';
import { useEmployeeDetails } from './hooks';

type EmployeeDetailsProps = {
  payflowContractId: number;
};

export const getContractFullName = (data: Employee) => {
  return `${data?.firstName} ${data?.lastName}`;
};

const EmployeeDetails = ({ payflowContractId }: EmployeeDetailsProps) => {
  const { t } = useTranslation(['flexflow', 'nav']);
  const pageTitle = t('employee details', { ns: 'flexflow' });
  const { isLoading, error, isError, dataWithStatus, suspendedByLimit, classes } = useEmployeeDetails({
    payflowContractId
  });

  if (isLoading) {
    return <PageWithTitleLoading title={pageTitle} />;
  }
  if (isError || !dataWithStatus) {
    // eslint-disable-next-line react/jsx-no-undef
    return <PageWithTitleError error={error} title={pageTitle} />;
  }

  const status = dataWithStatus?.status as unknown as ContractPayflowStatus;
  const cardStatus = dataWithStatus?.card?.status as unknown as FlexflowCardStatus;
  const statusesToCheck = [
    ContractFlexflowStatusToDisplay.SUSPENDED,
    ContractFlexflowStatusToDisplay.FROZEN,
    ContractFlexflowStatusToDisplay.REQUESTED
  ];
  const statusToDisplay = getStatusToDisplay(status, cardStatus);
  const isSuspendedBySwan = Boolean(dataWithStatus?.card?.physicalCard?.suspendedBySwanAt);

  return (
    <Page className={classes.root} title={pageTitle} dataTestId="flexflow-employee-details-page">
      <Container maxWidth={false}>
        <SubheaderWithButton
          pageTitle={getContractFullName(dataWithStatus as unknown as Employee)}
          data={dataWithStatus}
        />

        <Card>
          <CardContent>
            <Form initialValues={dataWithStatus} onSubmit={() => {}}>
              {() => (
                <EmployeeDetailsContent
                  data={dataWithStatus}
                  isLoading={false}
                  isError={false}
                  error={undefined}
                  isSuspendedBannerVisible={
                    statusesToCheck.includes(statusToDisplay) || !dataWithStatus?.isEligible || isSuspendedBySwan
                  }
                  suspendedByLimit={suspendedByLimit}
                />
              )}
            </Form>
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default EmployeeDetails;
